import {
  GraduationCapIcon,
  HashIcon,
  LayersIcon,
  LayoutDashboardIcon,
  LogOutIcon,
  MenuIcon,
  ArchiveIcon,
  CoinsIcon,
  CreditCardIcon,
  MessagesSquareIcon,
  RadioIcon,
  ShieldIcon,
  TerminalIcon,
  UserCogIcon,
  UsersIcon,
  XIcon,
  KeyRoundIcon,
  FileUpIcon,
  InboxIcon,
  ActivityIcon,
  BuildingIcon,
  UserIcon,
  Trash2Icon,
  UndoIcon,
  ScrollTextIcon,
  ShieldCheckIcon,
  ReceiptIcon,
} from 'lucide-react'
import { useEffect, useState, type ComponentType, type ReactNode } from 'react'
import { NavLink, Outlet, useLocation } from 'react-router'

import { OverLimitBanner } from '@/components/OverLimitBanner'
import { PastDueBanner } from '@/components/billing/PastDueBanner'
import { navAllowed, type NavAccess } from '@/lib/access'
import { useMe } from '@/api/queries'
import type { MembershipRole } from '@/api/types'
import { useAuth } from '@/auth/useAuth'
import { FeedbackDialog } from '@/components/FeedbackDialog'
import { Wordmark } from '@/components/Logo'
import { ThemeToggle } from '@/components/ThemeToggle'
import { ZoneSwitcher } from '@/components/ZoneSwitcher'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { env } from '@/lib/env'
import { cn } from '@/lib/utils'
import { canDo, parseTier, type Capability } from '@/staff/tier'
import { ViewAsBanner } from '@/staff/ViewAsBanner'
import { useReadOnly, useStaff, useViewAs } from '@/staff/hooks'
import { useActiveZone } from '@/zone/useActiveZone'

interface NavItem {
  to: string
  label: string
  description: string
  icon: ComponentType<{ className?: string }>
  end?: boolean
  /**
   * Who sees the entry (2026-10-BILLING-IN-APP BI4/BI5). `technical` hides it from a BILLING-role
   * member; `admin` is ADMIN-only (§18); `billing` is ADMIN, BILLING or a flagged USER; `users-read`
   * adds the read-only member list for BILLING.
   */
  access?: NavAccess
  /**
   * Rendered only when the server has a bucket configured (D31 decision 20):
   * without one the page would be an empty table explaining a feature that
   * cannot happen, which is worse than no entry.
   */
  requiresUploads?: boolean
  /** Rendered only for the platform-wide `saltseed-staff` role. */
  staffOnly?: boolean
  /** Rendered only when the caller's staff tier holds this capability (the Staff group). */
  capability?: Capability
}

/** Zone-scoped screens: they all read the switcher's selection. */
const ZONE_NAV: readonly NavItem[] = [
  {
    to: '/app/messages',
    label: 'Messages',
    description: 'The zone conversation',
    icon: MessagesSquareIcon,
  },
  {
    to: '/app/archive',
    label: 'Archive',
    description: 'Conversations put away',
    icon: ArchiveIcon,
  },
  {
    to: '/app/connections',
    label: 'Connections',
    description: 'Agents and humans in the zone',
    icon: RadioIcon,
  },
  {
    to: '/app/channels',
    label: 'Channels',
    description: 'Broadcast groups',
    icon: HashIcon,
  },
  {
    // §19. Zone-administrator-only, but the nav entry stays visible: the rule is
    // per zone, not per account role, so hiding it from a plain USER would also
    // hide it from the USER who administers the zone they are currently in.
    // The page itself explains the refusal.
    to: '/app/police',
    label: 'Police',
    description: 'Supervision and pausing',
    icon: ShieldIcon,
  },
  {
    // D30. The record that secrets were passed between agents -- never a value.
    to: '/app/secrets',
    label: 'Secrets',
    description: 'Passed between agents',
    icon: KeyRoundIcon,
  },
  {
    // D31. The record that files were passed between agents -- never the bytes.
    to: '/app/uploads',
    label: 'Uploads',
    description: 'Files passed between agents',
    icon: FileUpIcon,
    requiresUploads: true,
  },
  {
    to: '/app/setup',
    label: 'Setup',
    description: 'Wire clients to this zone',
    icon: TerminalIcon,
  },
]

/** Account-scoped screens: independent of the active zone. */
const ACCOUNT_NAV: readonly NavItem[] = [
  {
    to: '/app',
    label: 'Overview',
    description: 'Plan, usage, state',
    icon: LayoutDashboardIcon,
    end: true,
    access: 'technical',
  },
  {
    to: '/app/zones',
    label: 'Zones',
    description: 'Create, pause, archive',
    icon: LayersIcon,
    access: 'technical',
  },
  {
    to: '/app/users',
    label: 'Users',
    description: 'Invite and set roles',
    icon: UsersIcon,
    access: 'users-read',
  },
  {
    to: '/app/account',
    label: 'Account',
    description: 'Token, plan, limits',
    icon: UserCogIcon,
  },
  {
    to: '/app/plan',
    label: 'Plan',
    description: 'Subscription, seats, upgrade',
    icon: CreditCardIcon,
    access: 'billing',
  },
  {
    to: '/app/invoices',
    label: 'Invoices',
    description: 'Invoices, credit notes, pay',
    icon: ReceiptIcon,
    access: 'billing',
  },
  {
    to: '/app/billing',
    label: 'Police balance',
    description: 'Balance, top-ups, ledger',
    icon: CoinsIcon,
    access: 'billing',
  },
  {
    // Listed under the account, per the console's layout, but the bundle it
    // serves is per zone: the MCP URL and the server name both come from the
    // active zone, so the page resolves through RequireZone like Setup does.
    to: '/app/skills',
    label: 'Skills',
    description: 'Teach agents the protocol',
    icon: GraduationCapIcon,
    access: 'technical',
  },
]

/**
 * The Staff area (S2): platform-wide, shown only when `/api/me` reports a staff tier. Entries a
 * tier cannot open are not listed (the server refuses them anyway).
 */
const STAFF_NAV: readonly NavItem[] = [
  { to: '/app/staff', label: 'Health', description: 'Live platform numbers', icon: ActivityIcon, end: true, capability: 'directory_read' },
  { to: '/app/staff/accounts', label: 'Accounts', description: 'Directory and actions', icon: BuildingIcon, capability: 'directory_read' },
  { to: '/app/staff/users', label: 'Users', description: 'Find a person', icon: UserIcon, capability: 'directory_read' },
  { to: '/app/staff/ledger', label: 'Ledger', description: 'Balance entries, all accounts', icon: CoinsIcon, capability: 'directory_read' },
  { to: '/app/staff/refunds', label: 'Refund requests', description: 'Approve or reject refunds', icon: UndoIcon, capability: 'directory_read' },
  { to: '/app/staff/deletions', label: 'Deletions', description: 'The deletion queue', icon: Trash2Icon, capability: 'directory_read' },
  { to: '/app/staff/feedback', label: 'Feedback inbox', description: 'Reports sent to staff', icon: InboxIcon, capability: 'feedback_triage' },
  { to: '/app/staff/team', label: 'Team', description: 'Grant and revoke tiers', icon: ShieldCheckIcon, capability: 'staff_roles' },
  { to: '/app/staff/audit', label: 'Audit log', description: 'Every staff write', icon: ScrollTextIcon, capability: 'audit_read' },
]

function EnvironmentBadge() {
  const label = env.environment
  if (label === undefined || label === 'prod') return null
  return (
    <Badge variant="outline" className="font-mono text-[0.6875rem] tracking-wide">
      {label}
    </Badge>
  )
}

function NavGroup({
  title,
  items,
  viewer,
  uploadsEnabled,
  isStaff,
  staffTier,
  onNavigate,
}: {
  title: string
  items: readonly NavItem[]
  viewer: { role: MembershipRole | undefined; billing: boolean | undefined }
  uploadsEnabled: boolean
  isStaff: boolean
  staffTier?: string | null
  onNavigate?: () => void
}) {
  const visible = items.filter(
    (item) =>
      navAllowed(item.access, viewer) &&
      (item.requiresUploads !== true || uploadsEnabled) &&
      (item.staffOnly !== true || isStaff) &&
      (item.capability === undefined || canDo(parseTier(staffTier), item.capability)),
  )
  if (visible.length === 0) return null

  return (
    <div className="space-y-0.5">
      <p className="px-3 pb-2 text-[0.6875rem] font-medium tracking-[0.1em] text-muted-foreground uppercase">
        {title}
      </p>
      <nav className="flex flex-col gap-0.5" aria-label={title}>
        {visible.map(({ to, label, description, icon: Icon, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end ?? false}
            onClick={onNavigate}
            className={({ isActive }) =>
              cn(
                'group relative flex items-start gap-3 rounded-lg px-3 py-2.5 transition-colors',
                isActive
                  ? 'bg-[color-mix(in_oklch,var(--primary)_10%,transparent)] text-foreground'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground',
              )
            }
          >
            {({ isActive }) => (
              <>
                <span
                  aria-hidden="true"
                  className={cn(
                    'absolute top-2.5 bottom-2.5 -left-px w-0.5 rounded-full transition-colors',
                    isActive ? 'bg-primary' : 'bg-transparent',
                  )}
                />
                <Icon
                  className={cn('mt-0.5 size-4 shrink-0', isActive ? 'text-primary' : undefined)}
                />
                <span className="min-w-0">
                  <span className="block text-sm leading-tight font-medium">{label}</span>
                  <span className="mt-0.5 block truncate text-xs text-muted-foreground">
                    {description}
                  </span>
                </span>
              </>
            )}
          </NavLink>
        ))}
      </nav>
    </div>
  )
}

function SidebarBody({ onNavigate }: { onNavigate?: () => void }) {
  const me = useMe()
  const viewer = { role: me.data?.role, billing: me.data?.billing }
  const billingOnly = viewer.role === 'BILLING'
  const uploadsEnabled = me.data?.account.uploads_enabled === true
  const staff = useStaff()
  const isStaff = me.data?.staff === true || staff.tier !== null
  // A holder of the bare `saltseed-staff` role (no tier) keeps the feedback inbox and nothing else.
  const staffItems =
    staff.tier !== null
      ? STAFF_NAV
      : me.data?.staff === true
        ? STAFF_NAV.filter((item) => item.capability === 'feedback_triage')
        : []

  return (
    <div className="space-y-6">
      {billingOnly ? null : <ZoneSwitcher />}
      {billingOnly ? null : (
        <NavGroup
          title="Zone"
          items={ZONE_NAV}
          viewer={viewer}
          uploadsEnabled={uploadsEnabled}
          isStaff={isStaff}
          onNavigate={onNavigate}
        />
      )}
      <NavGroup
        title="Account"
        items={ACCOUNT_NAV}
        viewer={viewer}
        uploadsEnabled={uploadsEnabled}
        isStaff={isStaff}
        onNavigate={onNavigate}
      />
      {staffItems.length === 0 ? null : (
        <NavGroup
          title="Staff"
          items={staffItems}
          viewer={viewer}
          uploadsEnabled={uploadsEnabled}
          isStaff={isStaff}
          staffTier={staff.tier ?? 'support'}
          onNavigate={onNavigate}
        />
      )}
    </div>
  )
}

function AccountFooter() {
  const readOnly = useReadOnly()
  const { claims, logout } = useAuth()
  const me = useMe()
  const displayName = me.data?.user.name ?? claims?.name ?? claims?.email ?? 'Signed in'
  const secondary = me.data?.user.email ?? claims?.email

  return (
    <div className="space-y-3 border-t border-border px-4 py-4">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <p className="truncate text-sm font-medium text-foreground">{displayName}</p>
          {me.data === undefined ? null : (
            <Badge variant="secondary" className="font-mono text-[0.625rem]">
              {me.data.role}
            </Badge>
          )}
        </div>
        {secondary === undefined || secondary === displayName ? null : (
          <p className="truncate font-mono text-xs text-muted-foreground">{secondary}</p>
        )}
      </div>
      {readOnly ? null : <FeedbackDialog />}
      <div className="flex items-center gap-1">
        <Button
          variant="ghost"
          size="sm"
          onClick={logout}
          className="flex-1 justify-start text-muted-foreground hover:text-foreground"
        >
          <LogOutIcon className="size-4" />
          Sign out
        </Button>
        <ThemeToggle />
      </div>
    </div>
  )
}

/**
 * Raised once when a persisted zone selection stopped being reachable — an
 * archive, or a revoked grant. §18 answers those with 404, so without this the
 * user would just see pages fail for no stated reason.
 */
function LostZoneNotice() {
  const { lostPersistedZone, dismissLostZone, activeZone } = useActiveZone()
  if (!lostPersistedZone) return null

  return (
    <div className="flex items-start justify-between gap-4 border-b border-[color-mix(in_oklch,var(--accent)_35%,var(--border))] bg-[color-mix(in_oklch,var(--accent)_10%,var(--card))] px-6 py-3 lg:px-10">
      <p className="text-sm text-pretty text-foreground">
        The zone you were last working in is no longer available to you — it may have been
        archived, or your access to it revoked.{' '}
        {activeZone === undefined ? (
          'You have no other zone to fall back to.'
        ) : (
          <>
            Switched to <span className="font-mono">{activeZone.name}</span>.
          </>
        )}
      </p>
      <Button variant="ghost" size="sm" onClick={dismissLostZone}>
        Dismiss
      </Button>
    </div>
  )
}

export function AppShell() {
  const [mobileOpen, setMobileOpen] = useState(false)
  const location = useLocation()
  // Room for the fixed view-as bars at the foot of the viewport.
  const viewing = useViewAs() !== null

  useEffect(() => {
    setMobileOpen(false)
  }, [location.pathname])

  return (
    <div className={cn('min-h-dvh bg-background', viewing ? 'pb-24' : undefined)}>
      <ViewAsBanner />
      {/* Mobile top bar */}
      <header className="sticky top-0 z-40 flex h-14 items-center justify-between border-b border-border bg-background/90 px-4 backdrop-blur lg:hidden">
        <div className="flex items-center gap-2">
          <Wordmark className="text-foreground" />
          <EnvironmentBadge />
        </div>
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={() => {
            setMobileOpen((open) => !open)
          }}
          aria-label={mobileOpen ? 'Close navigation' : 'Open navigation'}
          aria-expanded={mobileOpen}
        >
          {mobileOpen ? <XIcon className="size-4" /> : <MenuIcon className="size-4" />}
        </Button>
      </header>

      {mobileOpen ? (
        <div className="border-b border-border bg-card px-3 py-3 lg:hidden">
          <SidebarBody
            onNavigate={() => {
              setMobileOpen(false)
            }}
          />
          <AccountFooter />
        </div>
      ) : null}

      <div className="lg:grid lg:grid-cols-[17.5rem_minmax(0,1fr)]">
        {/* Desktop sidebar */}
        <aside className="sticky top-0 hidden h-dvh flex-col border-r border-border bg-card lg:flex">
          <div className="flex h-16 items-center gap-2 border-b border-border px-5">
            <Wordmark className="text-foreground" />
            <EnvironmentBadge />
          </div>
          <div className="flex-1 overflow-y-auto px-3 py-4">
            <SidebarBody />
          </div>
          <AccountFooter />
        </aside>

        <main className="min-w-0">
          <OverLimitBanner />
          <PastDueBanner />
          <LostZoneNotice />
          <Outlet />
        </main>
      </div>
    </div>
  )
}

/**
 * Page-level header used inside the shell. Keeps the title block, supporting
 * copy and primary action aligned across every screen.
 */
export function PageHeader({
  eyebrow,
  title,
  description,
  actions,
}: {
  eyebrow?: string
  title: string
  description?: ReactNode
  actions?: ReactNode
}) {
  return (
    <div className="flex flex-col gap-4 border-b border-border px-6 py-8 sm:flex-row sm:items-end sm:justify-between lg:px-10 lg:py-10">
      <div className="min-w-0 space-y-2">
        {eyebrow === undefined ? null : (
          <p className="text-[0.6875rem] font-medium tracking-[0.12em] text-primary uppercase">
            {eyebrow}
          </p>
        )}
        <h1 className="text-2xl font-semibold tracking-[-0.02em] text-balance text-foreground">
          {title}
        </h1>
        {description === undefined ? null : (
          <div className="max-w-2xl text-sm text-pretty text-muted-foreground">
            {description}
          </div>
        )}
      </div>
      {actions === undefined ? null : (
        <div className="flex shrink-0 items-center gap-2">{actions}</div>
      )}
    </div>
  )
}

/** Standard content well below a {@link PageHeader}. */
export function PageBody({
  children,
  className,
}: {
  children: ReactNode
  className?: string
}) {
  return (
    <div className={cn('space-y-8 px-6 py-8 lg:px-10 lg:py-10', className)}>{children}</div>
  )
}
